import React, { useState, useRef, useEffect, useCallback } from 'react';
import { formatFilterPills } from '../utils/filterPills';

/**
 * Modal for managing saved searches.
 * Lists all saved searches with options to load, rename, delete, and toggle notifications.
 */
function SavedSearchesModal({
  isOpen,
  onClose,
  searches = [],
  onLoadSearch,
  onDeleteSearch,
  onUpdateSearch,
  onSaveCurrentSearch,
  onToggleNotifications,
  onExportSearch,
  currentFilters,
  isSubscriber = false,
  loading = false,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editOriginalName, setEditOriginalName] = useState('');
  const [saveName, setSaveName] = useState('');
  const modalRef = useRef(null);
  const nameInputRef = useRef(null);
  const saveInputRef = useRef(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Focus name input when editing
  useEffect(() => {
    if (editingId && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingId]);

  // Close on click outside
  const handleBackdropClick = useCallback((e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  }, [onClose]);

  // Start editing a search name
  const handleStartEdit = useCallback((search) => {
    setEditingId(search.id);
    setEditName(search.name);
    setEditOriginalName(search.name);
  }, []);

  // Save edited name
  const handleSaveEdit = useCallback(() => {
    if (editingId && editName.trim()) {
      onUpdateSearch(editingId, { name: editName.trim() }, editOriginalName);
      setEditingId(null);
      setEditName('');
      setEditOriginalName('');
    }
  }, [editingId, editName, editOriginalName, onUpdateSearch]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditName('');
    setEditOriginalName('');
  }, []);

  // Handle edit input keydown
  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  // Toggle notifications
  const handleToggleNotifications = useCallback((search) => {
    if (onToggleNotifications) {
      onToggleNotifications(search.id, !search.notifications_enabled, search.name);
    }
  }, [onToggleNotifications]);

  // Delete search
  const handleDelete = useCallback((search) => {
    onDeleteSearch(search.id, search.name);
  }, [onDeleteSearch]);

  // Load search and close modal
  const handleLoad = useCallback((search) => {
    onLoadSearch(search);
    onClose();
  }, [onLoadSearch, onClose]);

  // Save current search from modal
  const handleSaveFromModal = useCallback((e) => {
    e.preventDefault();
    if (saveName.trim()) {
      onSaveCurrentSearch(saveName.trim());
      setSaveName('');
    }
  }, [saveName, onSaveCurrentSearch]);

  // Format filter summary with slashes between filter types and commas within
  const formatFilterSummary = useCallback((filters) => {
    if (!filters) return 'No filters';
    
    const parts = [];
    if (filters.neighborhood?.length) {
      parts.push(filters.neighborhood.join(', '));
    }
    if (filters.uses?.length) {
      parts.push(filters.uses.join(', '));
    }
    if (filters.status?.length) {
      parts.push(filters.status.join(', '));
    }
    if (filters.company?.length) {
      parts.push(filters.company.join(', '));
    }
    if (filters.title) {
      parts.push(`"${filters.title}"`);
    }
    
    return parts.length > 0 ? parts.join(' / ') : 'All projects';
  }, []);

  // Format new projects count message
  const formatNewProjectsMessage = useCallback((count) => {
    if (count === 0) return 'No new updates';
    if (count === 1) return '1 new project matched';
    return `${count} new projects matched`;
  }, []);

  if (!isOpen) return null;

  const currentFilterPills = formatFilterPills(currentFilters);
  const hasCurrentFilters = currentFilterPills.length > 0;

  return (
    <div className="saved-searches-modal-backdrop" onClick={handleBackdropClick}>
      <div className="saved-searches-modal" ref={modalRef} role="dialog" aria-modal="true">
        <div className="saved-searches-modal__header">
          <h2>Saved Searches</h2>
          <button 
            className="saved-searches-modal__close" 
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path 
                d="M2 2L18 18M2 18L18 2" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="saved-searches-modal__content">
          {!isSubscriber ? (
            <div className="saved-searches-modal__upgrade">
              <div className="saved-searches-modal__upgrade-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h3>Save Your Searches</h3>
              <p>
                Upgrade to Pro to save unlimited searches, get daily email alerts 
                for new projects, and export data.
              </p>
              <a href="/subscribe" className="saved-searches-modal__upgrade-btn">
                Get Pro
              </a>
            </div>
          ) : (
            <>
              {/* Current View Filters section */}
              <div className="saved-searches-modal__current-filters">
                <div className="saved-searches-modal__current-filters-label">CURRENT VIEW FILTERS</div>
                <div className="saved-searches-modal__current-filters-pills">
                  {hasCurrentFilters ? (
                    currentFilterPills.map((pill, index) => (
                      <span key={index} className={`saved-searches-modal__filter-pill saved-searches-modal__filter-pill--${pill.type}`}>
                        {pill.label}
                      </span>
                    ))
                  ) : (
                    <span className="saved-searches-modal__filter-pill saved-searches-modal__filter-pill--empty">
                      No filters applied
                    </span>
                  )}
                </div>
              </div>

              {/* Save current search form */}
              <form className="saved-searches-modal__save-form" onSubmit={handleSaveFromModal}>
                <input
                  ref={saveInputRef}
                  type="text"
                  placeholder="Name this search..."
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  className="saved-searches-modal__save-input"
                />
                <button 
                  type="submit" 
                  className="saved-searches-modal__save-btn"
                  disabled={!saveName.trim() || loading}
                >
                  + Save
                </button>
              </form>

              {/* Search list */}
              {loading ? (
                <div className="saved-searches-modal__loading">
                  <div className="loading-spinner"></div>
                  <p>Loading saved searches...</p>
                </div>
              ) : searches.length === 0 ? (
                <div className="saved-searches-modal__empty">
                  <p>No saved searches yet.</p>
                  <p className="saved-searches-modal__empty-hint">
                    Apply filters to the map and save your search to access it later.
                  </p>
                </div>
              ) : (
                <ul className="saved-searches-modal__list">
                  {searches.map((search) => (
                    <li key={search.id} className="saved-search-item">
                      <div className="saved-search-item__main">
                        {editingId === search.id ? (
                          <div className="saved-search-item__edit">
                            <input
                              ref={nameInputRef}
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyDown={handleEditKeyDown}
                              className="saved-search-item__edit-input"
                            />
                            <button 
                              onClick={handleSaveEdit}
                              className="saved-search-item__edit-save"
                              title="Save"
                            >
                              ✓
                            </button>
                            <button 
                              onClick={handleCancelEdit}
                              className="saved-search-item__edit-cancel"
                              title="Cancel"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="saved-search-item__info">
                              <button
                                type="button"
                                className="saved-search-item__name-btn"
                                onClick={() => handleLoad(search)}
                                title="Load this search"
                                aria-label={`Load saved search: ${search.name}`}
                              >
                                {search.name}
                              </button>
                              <span className="saved-search-item__filters">
                                {formatFilterSummary(search.filters)}
                              </span>
                              <span className={`saved-search-item__status ${search.new_projects_count > 0 ? 'saved-search-item__status--has-updates' : ''}`}>
                                {formatNewProjectsMessage(search.new_projects_count || 0)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>

                      {editingId !== search.id && (
                        <div className="saved-search-item__actions">
                          {/* Order: notifications, export, rename, delete, view */}
                          {(() => {
                            const notifyTip = search.notifications_enabled
                              ? 'Disable email notifications'
                              : 'Enable email notifications';
                            return (
                              <button
                                className={`saved-search-item__btn saved-search-item__btn--notify ${search.notifications_enabled ? 'active' : ''}`}
                                onClick={() => handleToggleNotifications(search)}
                                data-tooltip={notifyTip}
                                aria-label={notifyTip}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                                </svg>
                                {search.notifications_enabled && (
                                  <svg className="saved-search-item__checkmark" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )}
                              </button>
                            );
                          })()}
                          {onExportSearch && (
                            <button
                              className="saved-search-item__btn saved-search-item__btn--export"
                              onClick={() => onExportSearch(search)}
                              data-tooltip="Export data"
                              aria-label="Export data"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                              </svg>
                            </button>
                          )}
                          <button
                            className="saved-search-item__btn saved-search-item__btn--rename"
                            onClick={() => handleStartEdit(search)}
                            data-tooltip="Rename"
                            aria-label="Rename"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            className="saved-search-item__btn saved-search-item__btn--delete"
                            onClick={() => handleDelete(search)}
                            data-tooltip="Delete"
                            aria-label="Delete"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                          <button
                            className="saved-search-item__btn saved-search-item__btn--view"
                            onClick={() => handleLoad(search)}
                            data-tooltip="Load this search"
                            aria-label="Load this search"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="11" cy="11" r="7" />
                              <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SavedSearchesModal;
